import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LogOut,
  Upload,
  Menu,
  UserCog,
  CreditCard,
  Settings,
  KeyRound,
  MessageSquare,
  BarChart3,
  Users,
  Wallet,
  Inbox,
} from "lucide-react";
import { CommandPaletteButton } from "@/components/shared/CommandPalette";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { SidebarNavContent } from "@/components/layout/Sidebar";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { TopBarWallet } from "@/components/layout/TopBarWallet";

interface TopBarProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export const TopBar: React.FC<TopBarProps> = ({ title, subtitle, actions }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const initials = (user?.name || user?.email || "U")
    .split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

  const isAdmin = user?.role === "admin";
  const isSuperAdmin = !!user?.is_super_admin;

  const go = (to: string) => () => navigate(to);

  return (
    <div
      data-testid="app-topbar"
      className="sticky top-0 z-30 bg-[color:var(--sg-card)]/85 backdrop-blur-md border-b border-[color:var(--sg-border)]"
    >
      <div className="flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-6 md:px-10 py-3 sm:py-4">
        {/* Mobile hamburger opens sidebar drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden h-9 w-9 rounded-md flex items-center justify-center border border-[color:var(--sg-border)] text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)] shrink-0"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" strokeWidth={2} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="p-0 w-72 max-w-[85vw] h-dvh flex flex-col"
            data-testid="mobile-menu-drawer"
          >
            <VisuallyHidden>
              <SheetTitle>Main navigation</SheetTitle>
              <SheetDescription>SociBaba dashboard menu</SheetDescription>
            </VisuallyHidden>
            <div
              data-testid="mobile-menu-scroll"
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
            >
              <SidebarNavContent
                onNavigate={() => setDrawerOpen(false)}
                showWallet
              />
            </div>
          </SheetContent>
        </Sheet>

        <div className="min-w-0 flex-1">
          <h1
            data-testid="page-title"
            className="font-display text-lg sm:text-2xl md:text-3xl font-semibold tracking-tight text-[color:var(--sg-fg)] leading-tight line-clamp-2 sm:line-clamp-1"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="hidden sm:block text-sm text-[color:var(--sg-muted)] mt-1.5 line-clamp-1">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {actions}
          {user ? (
            <>
              <div className="hidden md:block"><CommandPaletteButton /></div>
              <div className="md:hidden"><CommandPaletteButton compact /></div>
            </>
          ) : null}
          <ThemeToggle compact />
          {user ? <TopBarWallet /> : null}
          {user ? <NotificationBell /> : null}
          <Link to="/upload">
            <Button
              data-testid="topbar-new-content"
              size="sm"
              className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white dark:bg-[color:var(--sg-primary)] dark:text-[#0A1128] dark:hover:bg-[color:var(--sg-primary-hover)] dark:hover:text-[#0A1128] h-9 px-2 sm:px-3"
              title="New content"
            >
              <Upload className="h-3.5 w-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">New content</span>
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="topbar-user-menu"
                className="flex items-center gap-2 hover:bg-[color:var(--sg-muted-bg)] rounded-full p-0.5 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
                  <AvatarFallback className="bg-[color:var(--sg-fg)] text-white dark:bg-[color:var(--sg-primary)] dark:text-[#0A1128] text-xs font-semibold">
                    {initials || "SB"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-[color:var(--sg-card)]">
              <DropdownMenuLabel className="text-xs">
                <div className="font-semibold text-[color:var(--sg-fg)]">{user?.name}</div>
                <div className="text-[color:var(--sg-muted)] font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem data-testid="menu-profile" onClick={go("/profile")} className="cursor-pointer">
                <UserCog className="h-3.5 w-3.5 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-billing" onClick={go("/settings/billing")} className="cursor-pointer">
                <CreditCard className="h-3.5 w-3.5 mr-2" />
                Billing
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-settings" onClick={go("/settings")} className="cursor-pointer">
                <Settings className="h-3.5 w-3.5 mr-2" />
                Settings
              </DropdownMenuItem>

              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--sg-muted)] font-semibold">
                    Admin
                  </DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-admin-users" onClick={go("/admin/users")} className="cursor-pointer">
                    <Users className="h-3.5 w-3.5 mr-2" />
                    Team & Roles
                  </DropdownMenuItem>
                </>
              )}

              {isSuperAdmin && (
                <>
                  {!isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--sg-muted)] font-semibold">
                        Super Admin
                      </DropdownMenuLabel>
                    </>
                  )}
                  <DropdownMenuItem data-testid="menu-admin-inbox" onClick={go("/superadmin/inbox")} className="cursor-pointer">
                    <Inbox className="h-3.5 w-3.5 mr-2" />
                    SMS / WhatsApp Inbox
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-contact-inbox" onClick={go("/admin/contact-inbox")} className="cursor-pointer">
                    <Inbox className="h-3.5 w-3.5 mr-2" />
                    Contact Inbox
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-feedback" onClick={go("/admin/feedback")} className="cursor-pointer">
                    <MessageSquare className="h-3.5 w-3.5 mr-2" />
                    Feedback Inbox
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-billing-plans" onClick={go("/admin/billing-plans")} className="cursor-pointer">
                    <KeyRound className="h-3.5 w-3.5 mr-2" />
                    Plan Prices
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-gift" onClick={go("/admin/gift")} className="cursor-pointer">
                    <MessageSquare className="h-3.5 w-3.5 mr-2" />
                    Gift Entries
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-gift-products" onClick={go("/admin/gift-products")} className="cursor-pointer">
                    <MessageSquare className="h-3.5 w-3.5 mr-2" />
                    Gift Products
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-setup" onClick={go("/admin/setup")} className="cursor-pointer">
                    <KeyRound className="h-3.5 w-3.5 mr-2" />
                    Admin Setup
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-testimonials" onClick={go("/admin/testimonials")} className="cursor-pointer">
                    <MessageSquare className="h-3.5 w-3.5 mr-2" />
                    Wall of Comments
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-subs" onClick={go("/admin/subscriptions")} className="cursor-pointer">
                    <BarChart3 className="h-3.5 w-3.5 mr-2" />
                    Subscriptions
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-payouts" onClick={go("/admin/payouts")} className="cursor-pointer">
                    <Wallet className="h-3.5 w-3.5 mr-2" />
                    Referral Payouts
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-admin-utm" onClick={go("/admin/utm-report")} className="cursor-pointer">
                    <BarChart3 className="h-3.5 w-3.5 mr-2" />
                    UTM Report
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer">
                <LogOut className="h-3.5 w-3.5 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
